import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { providerProfileApi, ApiError } from '../api';
import type { ProviderProfile, ProviderDashboardData, ProviderLocation } from '../types/provider';

interface ProviderProfileState {
	// Profile data
	profile: ProviderProfile | null;
	selectedLocationId: number | null;
	// Dashboard data
	dashboard: ProviderDashboardData | null;
	// Loading states
	isLoadingProfile: boolean;
	isLoadingDashboard: boolean;
	// Error states
	profileError: string | null;
	dashboardError: string | null;
	// Actions
	fetchProfile: () => Promise<void>;
	fetchDashboard: (locationId: number) => Promise<void>;
	setSelectedLocation: (locationId: number) => void;
	setPhoneVerified: (verified: boolean) => void;
	clearProfile: () => void;
	// Computed getters
	getSelectedLocation: () => ProviderLocation | null;
	getPrimaryLocation: () => ProviderLocation | null;
}

const useProviderProfileStore = create<ProviderProfileState>()(
	devtools(
		(set, get) => ({
			// Initial state
			profile: null,
			selectedLocationId: null,
			dashboard: null,
			isLoadingProfile: false,
			isLoadingDashboard: false,
			profileError: null,
			dashboardError: null,

			// Fetch provider profile from API
			fetchProfile: async () => {
				set({ isLoadingProfile: true, profileError: null });

				try {
					const profile = await providerProfileApi.getProfile();

					// Set profile and auto-select primary location
					const primaryLocation = profile.locations?.find(loc => loc.primary);
					const selectedLocationId =
						primaryLocation?.id || profile.locations?.[0]?.id || null;

					set({
						profile,
						selectedLocationId,
						isLoadingProfile: false,
					});
				} catch (error) {
					const apiError = error as ApiError;
					set({
						profileError: apiError.message || 'Failed to load profile',
						isLoadingProfile: false,
					});

					if (import.meta.env.DEV) {
						// eslint-disable-next-line no-console
						console.error('Provider profile fetch error:', apiError);
					}
				}
			},

			// Fetch dashboard data for a specific location
			fetchDashboard: async (locationId: number) => {
				set({ isLoadingDashboard: true, dashboardError: null });

				try {
					const dashboard = await providerProfileApi.getDashboard(locationId);

					set({
						dashboard,
						isLoadingDashboard: false,
					});
				} catch (error) {
					const apiError = error as ApiError;
					set({
						dashboardError: apiError.message || 'Failed to load dashboard',
						isLoadingDashboard: false,
					});

					if (import.meta.env.DEV) {
						// eslint-disable-next-line no-console
						console.error('Provider dashboard fetch error:', apiError);
					}
				}
			},

			// Set selected location (also clears dashboard so it can be refetched)
			setSelectedLocation: (locationId: number) => {
				set({
					selectedLocationId: locationId,
					dashboard: null, // Clear dashboard when location changes
				});
			},

			// Update phoneVerified flag in profile
			setPhoneVerified: (verified: boolean) => {
				const currentProfile = get().profile;
				if (currentProfile) {
					set({
						profile: {
							...currentProfile,
							phoneVerified: verified,
						},
					});
				}
			},

			// Clear all data (on logout)
			clearProfile: () => {
				set({
					profile: null,
					selectedLocationId: null,
					dashboard: null,
					isLoadingProfile: false,
					isLoadingDashboard: false,
					profileError: null,
					dashboardError: null,
				});
			},

			// Get currently selected location
			getSelectedLocation: () => {
				const { profile, selectedLocationId } = get();
				if (!profile?.locations || !selectedLocationId) return null;
				return profile.locations.find(loc => loc.id === selectedLocationId) || null;
			},

			// Get primary location
			getPrimaryLocation: () => {
				const { profile } = get();
				if (!profile?.locations || profile.locations.length === 0) return null;
				return profile.locations.find(loc => loc.primary) || profile.locations[0];
			},
		}),
		{ name: 'provider-profile-store' }
	)
);

export default useProviderProfileStore;
